import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { match } from 'ts-pattern';
import { type GovernanceConfig, GovernanceConfigForm } from './GovernanceConfigForm';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/mspr/governance-config/edit/$id')({
    component: EditGovernanceConfig,
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
});

function EditGovernanceConfig() {
    const { id: configId } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const [open, setOpen] = useState(true);
    const [data, setData] = useState<GovernanceConfig>();

    const { data: governanceConfig, isPending: isLoading } = trpc.mspr.governanceConfig.getGovernanceConfig.useQuery(
        {
            id: configId,
        },
        { refetchOnWindowFocus: false },
    );

    const { mutate: updateGovernanceConfig, isPending: isUpdating } =
        trpc.mspr.governanceConfig.updateGovernanceConfig.useMutation();

    useEffect(() => {
        if (governanceConfig) {
            setData(governanceConfig);
        }
    }, [governanceConfig]);

    const handleSubmit = (values: GovernanceConfig) => {
        setData(values);
        if (!configId) return;
        message.loading('Updating governance configuration...');
        updateGovernanceConfig(
            {
                id: configId,
                fieldName: values.fieldName ?? '',
            },
            {
                onSuccess: () => {
                    message.success('Governance configuration updated');
                    trpcUtils.mspr.governanceConfig.listGovernanceConfigs.invalidate();
                    setOpen(false);
                },
            },
        );
    };

    const state = match({ isLoading, isUpdating })
        .with({ isLoading: true }, () => 'loading' as const)
        .with({ isUpdating: true }, () => 'submitting' as const)
        .otherwise(() => 'ready' as const);

    return (
        <PageController route={Route}>
            <GovernanceConfigForm
                open={open}
                mode="edit"
                data={data}
                state={state}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/configuration/compliance/mspr/governance-config',
                        params: { orgId },
                    });
                }}
            />
        </PageController>
    );
}
